import React, { useState, useEffect, useRef } from 'react';
import Icon from '../../../components/AppIcon';

const SocialProofSection = () => {
  const [headerVisible, setHeaderVisible] = useState(false);
  const [cardsVisible, setCardsVisible] = useState([]);
  const sectionRef = useRef(null);

  useEffect(() => {
    const headerObs = new IntersectionObserver(
      (entries) => { if (entries[0].isIntersecting) { setHeaderVisible(true); headerObs.disconnect(); } },
      { threshold: 0.2 }
    );
    const cardsObs = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          [0, 1, 2, 3].forEach((i) => setTimeout(() => setCardsVisible(prev => [...prev, i]), i * 130));
          cardsObs.disconnect();
        }
      },
      { threshold: 0.15 }
    );
    if (sectionRef.current) {
      const headerEl = sectionRef.current.querySelector('#sp-header');
      const cardsEl = sectionRef.current.querySelector('#sp-cards');
      if (headerEl) headerObs.observe(headerEl);
      if (cardsEl) cardsObs.observe(cardsEl);
    }
    return () => { headerObs.disconnect(); cardsObs.disconnect(); };
  }, []);

  const goals = [
    {
      icon: 'Sun',
      emoji: '🌞',
      title: 'Manage Emotions',
      description: 'For understanding your feelings, calming your inner world, and responding instead of reacting.',
      color: 'from-yellow-500/20 to-orange-500/20',
      iconColor: 'text-yellow-500'
    },
    {
      icon: 'Compass',
      emoji: '🧘',
      title: 'Connect to Source',
      description: 'For feeling grounded, guided, and connected to something deeper than daily stress.',
      color: 'from-violet-500/20 to-purple-500/20',
      iconColor: 'text-violet-400'
    },
    {
      icon: 'Sparkles',
      emoji: '🌈',
      title: 'Attract Abundance',
      description: 'For shifting limiting beliefs, building self-worth, and opening yourself to growth and possibility.',
      color: 'from-emerald-500/20 to-teal-500/20',
      iconColor: 'text-emerald-400'
    },
    {
      icon: 'Star',
      emoji: '✨',
      title: 'All of the Above',
      description: 'For a holistic experience that supports emotional healing, spiritual connection, and personal expansion.',
      color: 'from-blue-500/20 to-cyan-500/20',
      iconColor: 'text-blue-400'
    }
  ];

  return (
    <section ref={sectionRef} className="relative py-20 px-6 bg-background/50 overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <img
          src="/image2.jpg"
          alt=""
          aria-hidden="true"
          className="w-full h-full object-cover object-center opacity-[0.18] scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-background/40 via-transparent to-background/40" />
      </div>
      <div className="relative z-10 max-w-6xl mx-auto">
        {/* Section Header */}
        <div id="sp-header" className={`text-center mb-16 transition-all duration-1000 ${headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <h2 className="text-3xl md:text-5xl font-bold text-foreground mb-6">
            What do you want to{' '}
            <span className="text-gradient">work on?</span>
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            When you start DepthMe, you choose your focus. Your survey answers and voice guide
            personalize everything from there.
          </p>
        </div>

        {/* Goal Cards */}
        <div id="sp-cards" className="grid md:grid-cols-2 gap-6">
          {goals.map((goal, index) => (
            <div
              key={index}
              className={`glassmorphism p-8 rounded-3xl transition-all duration-700 ${
                cardsVisible.includes(index) ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
              }`}
              style={{ transitionDelay: `${index * 110}ms` }}
            >
              <div className="flex items-start space-x-4">
                <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${goal.color} flex items-center justify-center flex-shrink-0`}>
                  <span className="text-2xl">{goal.emoji}</span>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-foreground mb-2">{goal.title}</h3>
                  <p className="text-muted-foreground leading-relaxed">{goal.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Note */}
        <div className="text-center mt-12">
          <p className="text-sm text-muted-foreground max-w-xl mx-auto">
            You can always explore other goals later. DepthMe starts in the right place for you and adapts as you grow.
          </p>
        </div>
      </div>
    </section>
  );
};

export default SocialProofSection;
